import React, { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'redux/store';
import { Box, Container, Grid, Typography } from '@material-ui/core';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import { getCareerLevelByValue, getSalaryByValue } from 'constants/constants';
import { DegreeEnum } from 'core/enum/degree';
import useAuth from 'hooks/useAuth';
import { companySizeChoices } from 'core/constants/aboutMePopUpConstants';
import { contractTypes } from 'core/constants/workType';
import { ProfileService } from 'services/profile/profile';
import { SkillService } from 'services/skills/skillService';
import { Industries } from 'core/interface/education-side';
import { ILocationRedux, IndustryRedux } from 'core/interface/education';
import { CompanyService } from 'services/companes/companies';
import {
  JobCard,
  JobDetailWrapper,
  JobDescription,
  CompetencyTitle,
  CompetencyContainer,
  CompetencyTag
} from '.';
import colors from 'assets/variables/colors';

const RootStyle = styled(Box)(() => ({
  position: 'relative',
  marginTop: '0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh'
}));

const WrapperContent = styled(Box)(() => ({
  position: 'relative',
  flex: 1
}));

const GridStyle = styled(Grid)(() => ({
  '@media (max-width: 959px)': {
    flexWrap: 'wrap',
    rowGap: '20px'
  }
}));

const JobTitle = styled(Typography)(() => ({
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: '24px',
  color: '#131417',
  marginBottom: '8px'
}));

const PublicJobDetail: FC = (): JSX.Element => {
  const { t } = useLocales();
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { singleJob: job } = useSelector((state: RootState) => state.jobs);
  const { isAuthenticated, isCandidate, isCompanyUser } = useAuth();
  const industries = useSelector(
    (selector: IndustryRedux) => selector.industries?.industry || []
  );
  const { computerSkills: skills } = useSelector(
    (selector: RootState) => selector.computerSkills || []
  );
  const cities = useSelector(
    (selector: ILocationRedux) => selector.locations?.cities || []
  );

  const { publicCompanies } = useSelector((state: RootState) => state.company);

  // State for job details
  const [jobIndustries, setJobIndustries] = useState<string[]>([]);
  const [jobLocations, setJobLocations] = useState<string[]>([]);
  const [jobSkills, setJobSkills] = useState<string[]>([]);
  const [disableActions, setDisableActions] = useState(false);
  const [isCompanyPublic, setIsCompanyPublic] = useState(false);

  useEffect(() => {
    if (slug) {
      getJobByIdOrSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    // Fetch necessary data for displaying job details
    getIndustries();
    getCities();
    getSkills();
    getPublicCompanies();
  }, []);

  useEffect(() => {
    // Map industries when both job and industries data are available
    if (job?.industries?.length && industries?.length) {
      mapJobIndustries();
    }
  }, [job, industries]);

  useEffect(() => {
    // Map locations when both job and cities data are available
    if (job?.locations?.length && cities?.length) {
      mapJobLocations();
    }
  }, [job, cities]);

  useEffect(() => {
    // Map skills when both job and skills data are available
    if (job?.skills?.length && skills?.length) {
      mapJobSkills();
    }
  }, [job, skills]);

  useEffect(() => {
    // Check if job company exists in publicCompanies
    if (job?.company?.id && publicCompanies?.results?.length) {
      const companyExistsAndHasCareerPage = publicCompanies.results.some(
        (company: any) =>
          company.id === job.company.id && company?.has_career_page
      );
      setIsCompanyPublic(companyExistsAndHasCareerPage);
    }
  }, [job, publicCompanies]);

  const getJobByIdOrSlug = (param: number | string): void => {
    JobService.getJobByIdOrSlug(param);
  };

  const getIndustries = (): void => {
    ProfileService.getIndustries();
  };

  const getCities = (limit: number = 20919, q?: string) => {
    JobService.getLocations({
      limit,
      q
    });
  };

  const getSkills = (query: string = ''): void => {
    SkillService.getITSkillsList(query);
  };

  const getPublicCompanies = (): void => {
    CompanyService.getPublicCompanies();
  };

  const mapJobIndustries = (): void => {
    const foundIndustries: string[] = [];
    industries?.forEach((industry: Industries) => {
      job?.industries?.forEach((jobIndustry: number) => {
        if (industry.id === jobIndustry) {
          foundIndustries.push(industry.name);
        }
      });
    });
    setJobIndustries(foundIndustries);
  };

  const mapJobLocations = (): void => {
    const foundLocations: string[] = [];
    if (!cities?.length || !job?.locations?.length) {
      setJobLocations([]);
      return;
    }

    cities?.forEach((city: any) => {
      job?.locations?.forEach((jobLocation: any) => {
        // Handle both cases: when jobLocation is an ID or an object with an ID
        const locationId =
          typeof jobLocation === 'object' ? jobLocation.id : jobLocation;
        if (city.id === locationId) {
          foundLocations.push(city.display_name);
        }
      });
    });
    setJobLocations(foundLocations);
  };

  const mapJobSkills = (): void => {
    const foundSkills: string[] = [];
    skills?.forEach((skill: any) => {
      job?.skills?.forEach((jobSkill: number) => {
        if (skill.id === jobSkill) {
          foundSkills.push(skill.name);
        }
      });
    });
    setJobSkills(foundSkills);
  };

  const handleApply = async (jobId: number) => {
    setDisableActions(true);
    try {
      const res = await JobService.applyForAJob(jobId);
      if (res) {
        // Show success notification
        console.log('Applied successfully');
        // Refresh job data to update the is_applied status
        getJobByIdOrSlug(jobId);
      } else {
        // Show error notification
        setDisableActions(false);
        console.log('Error applying for job');
      }
    } catch (error) {
      setDisableActions(false);
      console.error('Error applying for job:', error);
    }
  };

  const handleNavigateToLogin = () => {
    navigate('/login?role=candidate');
  };

  const handleCompanyClick = () => {
    if (isCompanyPublic && (job.company.slug || job?.company?.id)) {
      navigate(`/companies/${job.company.slug || job.company.id}`);
    }
  };

  // Find company size display name
  const getCompanySizeDisplay = (sizeValue: string) => {
    const size = companySizeChoices.find(
      (s) => String(s.value) === String(sizeValue)
    );
    return size ? size.to_show : '';
  };

  // Find contract type display name
  const getContractTypeDisplay = (contractValue: number | number[]) => {
    if (Array.isArray(contractValue)) {
      return contractValue
        .map((val) => {
          const contract = contractTypes.find((c) => c.value === val);
          return contract ? contract.to_show : '';
        })
        .filter(Boolean)
        .join(', ');
    }
    const contract = contractTypes.find((c) => c.value === contractValue);
    return contract ? contract.to_show : '';
  };

  // Check if job data is loaded
  if (!job) {
    return (
      <RootStyle>
        <Navbar />
        <WrapperContent>
          <Container
            maxWidth="lg"
            style={{ paddingTop: '50px', paddingBottom: '50px' }}
          >
            <Typography variant="h5" align="center">
              {t('Loading job details...')}
            </Typography>
          </Container>
        </WrapperContent>
        <Footer />
      </RootStyle>
    );
  }

  return (
    <RootStyle>
      <Navbar />
      <WrapperContent>
        <Container
          maxWidth="lg"
          style={{ paddingTop: '50px', paddingBottom: '50px' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 3,
              cursor: 'pointer'
            }}
            onClick={() => navigate(-1)}
          >
            <Image name="back.svg" width={16} height={16} />
            <Typography
              variant="body1"
              style={{ marginLeft: '10px', color: colors.tenantColor }}
            >
              {t('Back to public jobs')}
            </Typography>
          </Box>

          <GridStyle container columnGap="38px" wrap="nowrap">
            <Grid item xs={12} md={9} lg={8}>
              <JobCard style={{ cursor: 'default' }}>
                <Grid
                  item
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start'
                  }}
                >
                  <Grid display="flex" alignItems="start" width="100%">
                    <Grid item xs={7} sm={8}>
                      <JobTitle variant="h5">{job?.title}</JobTitle>
                      <JobDescription
                        variant="body2"
                        dangerouslySetInnerHTML={{ __html: job?.description }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={5}
                      sm={4}
                      style={{ textAlign: 'right', height: '80px' }}
                    >
                      <Box
                        onClick={
                          isCompanyPublic ? handleCompanyClick : undefined
                        }
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          height: '100%'
                        }}
                      >
                        <Image
                          name={
                            job?.company?.logo ||
                            '/static/images/recrout-logo-letter.jpg'
                          }
                          url={
                            job?.company?.logo ||
                            '/static/images/recrout-logo-letter.jpg'
                          }
                          style={{
                            width: 'auto',
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '80px',
                            objectFit: 'contain',
                            cursor: isCompanyPublic ? 'pointer' : 'default'
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <CompetencyTitle>
                    {t('We are looking for these competencies')}:
                  </CompetencyTitle>
                  {/* Competency tags */}
                  {job?.competencies && job?.competencies.length > 0 && (
                    <CompetencyContainer>
                      {job.competencies.map((comp: any, index: number) => (
                        <CompetencyTag
                          key={index}
                          tenantColor={colors.tenantColor}
                          lighterPurple={colors.lightPurple}
                        >
                          {comp.competency.name}
                        </CompetencyTag>
                      ))}
                    </CompetencyContainer>
                  )}

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <JobDetailWrapper>
                        <strong>{t('Company')}: </strong>
                        {job?.company?.name}
                      </JobDetailWrapper>

                      {jobLocations.length > 0 && (
                        <JobDetailWrapper>
                          <strong>{t('Location')}: </strong>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {jobLocations.join(', ')}
                          </div>
                        </JobDetailWrapper>
                      )}

                      {job?.contract_type && (
                        <JobDetailWrapper>
                          <strong>{t('Contract')}: </strong>
                          {getContractTypeDisplay(job.contract_type)}
                        </JobDetailWrapper>
                      )}

                      {job?.is_remote !== undefined && (
                        <JobDetailWrapper>
                          <strong>{t('Remote Work')}: </strong>
                          {job.is_remote ? t('Yes') : t('No')}
                        </JobDetailWrapper>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {job?.degree && (
                        <JobDetailWrapper>
                          <strong>{t('Education')}: </strong>
                          {DegreeEnum[job.degree]}
                        </JobDetailWrapper>
                      )}

                      {job?.work_exp && (
                        <JobDetailWrapper>
                          <strong>{t('Experience')}: </strong>
                          {getCareerLevelByValue(job.work_exp)}
                        </JobDetailWrapper>
                      )}

                      {job?.company?.company_size && (
                        <JobDetailWrapper>
                          <strong>{t('Company Size')}: </strong>
                          {getCompanySizeDisplay(job.company.company_size)}
                        </JobDetailWrapper>
                      )}

                      {job?.salary && (
                        <JobDetailWrapper>
                          <strong>{t('Salary')}: </strong>
                          {Array.isArray(job.salary)
                            ? job.salary
                                .map((sal: number) => getSalaryByValue(sal))
                                .filter(Boolean)
                                .join(', ')
                            : getSalaryByValue(job.salary)}
                        </JobDetailWrapper>
                      )}
                    </Grid>
                  </Grid>

                  {/* Additional sections */}
                  {jobIndustries.length > 0 && (
                    <Box mt={3}>
                      <JobDetailWrapper>
                        <strong>{t('Industries')}: </strong>
                        {jobIndustries.join(', ')}
                      </JobDetailWrapper>
                    </Box>
                  )}

                  {jobSkills.length > 0 && (
                    <Box mt={2}>
                      <JobDetailWrapper>
                        <strong>{t('Skills')}: </strong>
                        {jobSkills.join(', ')}
                      </JobDetailWrapper>
                    </Box>
                  )}

                  {job?.musts && job.musts.length > 0 && (
                    <Box mt={2}>
                      <JobDetailWrapper>
                        <strong>{t('Must Haves')}: </strong>
                        {job.musts.join(', ')}
                      </JobDetailWrapper>
                    </Box>
                  )}

                  {job?.perks && job.perks.length > 0 && (
                    <Box mt={2}>
                      <JobDetailWrapper>
                        <strong>{t('Perks')}: </strong>
                        {job.perks.join(', ')}
                      </JobDetailWrapper>
                    </Box>
                  )}
                </Grid>
              </JobCard>
            </Grid>
            <Grid item xs={12} md={3} lg={4}>
              <Box
                sx={{
                  background: '#fff',
                  borderRadius: '25px',
                  padding: '30px',
                  boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.08)',
                  color: '#131417'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('Job Information')}
                </Typography>

                {jobIndustries.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Industry')}
                    </Typography>
                    <Typography variant="body2">
                      {jobIndustries.join(', ')}
                    </Typography>
                  </Box>
                )}

                {jobLocations.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Location')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-word',
                        display: 'flex',
                        flexWrap: 'wrap'
                      }}
                    >
                      {jobLocations.join(', ')}
                    </Typography>
                  </Box>
                )}

                {job?.degree && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Required Degree')}
                    </Typography>
                    <Typography variant="body2">
                      {DegreeEnum[job.degree]}
                    </Typography>
                  </Box>
                )}

                {job?.work_exp && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Career Level')}
                    </Typography>
                    <Typography variant="body2">
                      {getCareerLevelByValue(job.work_exp)}
                    </Typography>
                  </Box>
                )}

                {job?.contract && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Contract Type')}
                    </Typography>
                    <Typography variant="body2">
                      {getContractTypeDisplay(job.contract)}
                    </Typography>
                  </Box>
                )}

                {job?.compensation && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Compensations (EUR)')}
                    </Typography>
                    <Typography variant="body2">
                      {Array.isArray(job.compensation)
                        ? job.compensation
                            .map((sal: number) => getSalaryByValue(sal))
                            .filter(Boolean)
                            .join(', ')
                        : getSalaryByValue(job.compensation)}
                    </Typography>
                  </Box>
                )}

                {/* Company size - added as requested */}
                {job?.company?.company_size && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Company Size')}
                    </Typography>
                    <Typography variant="body2">
                      {getCompanySizeDisplay(job.company.company_size)}
                    </Typography>
                  </Box>
                )}

                {job?.is_remote !== undefined && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Remote Work')}
                    </Typography>
                    <Typography variant="body2">
                      {job.is_remote ? t('Yes') : t('No')}
                    </Typography>
                  </Box>
                )}

                {jobSkills.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('Skills')}
                    </Typography>
                    <Typography variant="body2">
                      {jobSkills.join(', ')}
                    </Typography>
                  </Box>
                )}

                {/* Apply button with conditional rendering based on user role */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  {!isAuthenticated ? (
                    <Button
                      onClick={handleNavigateToLogin}
                      t={t}
                      className="navbar__left-btn"
                      title={t('Login or Sign up to apply')}
                    />
                  ) : isCandidate && !disableActions ? (
                    <Button
                      onClick={() => handleApply(job?.id)}
                      t={t}
                      className="navbar__left-btn"
                      disabled={job?.is_applied}
                      title={job?.is_applied ? t('Applied') : t('Apply Now')}
                    />
                  ) : isCompanyUser ? (
                    // No apply button for company users
                    <Typography variant="body2" color="textSecondary">
                      {t('Logged in as a company user')}
                    </Typography>
                  ) : (
                    !disableActions && (
                      <Button
                        onClick={() => handleApply(job?.id)}
                        t={t}
                        className="navbar__left-btn"
                        disabled={job?.is_applied}
                        title={job?.is_applied ? t('Applied') : t('Apply Now')}
                      />
                    )
                  )}
                </Box>
              </Box>
            </Grid>
          </GridStyle>
        </Container>
      </WrapperContent>
      <Footer />
    </RootStyle>
  );
};

export default PublicJobDetail;
